/**
 * Works out which part of a rule prompt is the phrase that actually matters, so the board banner can
 * set it in bold: "Schnappe dir alles **Gemüse**!".
 *
 * Why: the prompt is read aloud, but the voice is silent in languages with no matching TTS voice,
 * on a muted device, and after the child's first tap (which cancels it). Everything except the key
 * phrase is boilerplate — "Schnappe dir alle …" is the same on every board — so a child who has to
 * READ the banner is reading four words of noise to find the part that carries the rule.
 *
 * The key phrase comes from the class's own label, which is why this takes both. The label rarely
 * appears verbatim, though — German inflects it (Kleidung → Kleider), compounds it (Küche →
 * Küchensachen) or hyphenates it (Natur → Natur-Dinge) — so every WORD of the label is matched on
 * its own, walking progressively shorter prefixes and taking the longest that occurs. The bold then
 * spans from the first matched word to the last, glue and all: "**Schlangen und Reptilien**".
 *
 * Matching the label as one string instead is what this used to do, and it is why a phrase label
 * only ever got its first word bolded — "Wilde Natur" cannot match "wilden Natur-Wörter" whole, so
 * it fell back to the prefix "wilde" and stopped there.
 *
 * Some classes share no wording with their prompt at all: "Sonnensystem" is announced as "Sonne,
 * Mond und Planeten", an enumeration no amount of stemming can recover. Those carry an explicit
 * `emphasis` string in the pool JSON, which wins over everything here.
 */

export interface PromptParts {
  before: string;
  bold: string;
  after: string;
}

/**
 * How short a *truncated* prefix may get before it stops being evidence of anything — "Ba" would
 * match half a German sentence. The full label word is exempt (see the loop): a word that is simply
 * two characters long is an exact word, not a guess at one.
 *
 * Three letters is the right floor for an alphabet. It is far too high for a script where one
 * character is a whole syllable or morpheme: every Korean label in the game is one or two Hangul
 * syllables ("색깔", "몸"), so a floor of three silenced Korean almost entirely — 16 of the 18
 * Daily Life topics — and cost Thai and Japanese a few topics each.
 */
const MIN_STEM = 3;
const MIN_STEM_DENSE = 2;

/**
 * Scripts whose single character carries about as much as a short Latin syllable: Hangul, kana,
 * Han, and Thai. Tested against the label word, not the prompt — it is the label being truncated.
 */
const DENSE_SCRIPT = /[\p{Script=Hangul}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Han}\p{Script=Thai}]/u;

/**
 * Evidence needed to pull a SECOND word into an already-started bold run: the whole label word, or
 * a prefix at least this long. Three letters is enough to start a run but not to extend one —
 * "Piraten & Schätze" truncates "Schätze" to "Sch", which opens "Schnappe dir alle …", and joining
 * on that would have set the entire prompt in bold.
 */
const MIN_JOIN_STEM = 4;

/**
 * Glue allowed to sit INSIDE a bold run, between two matched label words: "Schlangen **und**
 * Reptilien", "serpenti **e i** rettili". Two short function words is the most any shipped language
 * needs; past that the two matches are separate parts of the sentence, not one phrase.
 */
const MAX_GAP_WORDS = 2;
const MAX_GAP_WORD_LEN = 4;
const SENTENCE_BREAK = /[.!?。！？]/u;

/**
 * A match is grown out to whitespace on both sides so the whole compound is emphasised, not the
 * fragment that happened to match — "Küchensachen", never "Küchen". Japanese and Thai are written
 * without spaces, so there is no boundary to stop at and that growth would swallow the entire
 * prompt; past this share of the string we keep the matched substring instead. Spaced prompts do
 * not need the cap — whitespace already bounds each token, and the join rule above bounds the run —
 * and applying it there would truncate legitimately long phrases ("slangen en reptielen" is two
 * thirds of "Pak alle slangen en reptielen!").
 */
const MAX_TOKEN_SHARE = 0.6;

const TRIM_LEAD = /^[\s¡¿]+/u;
const TRIM_TAIL = /[\s!?.,、。！？：:;]+$/u;

interface Match {
  /** Bounds after growing out to the surrounding token — what would actually be set in bold. */
  start: number;
  end: number;
  /** The literal stem hit, before that growth. */
  at: number;
  len: number;
  /** Whether the stem covered the whole label word; a partial hit is weaker evidence. */
  whole: boolean;
  /** Whether the stem reached the stem floor; below it the word is glue ("de", "e", "en"). */
  content: boolean;
}

function grow(
  prompt: string, at: number, len: number, spaced: boolean, whole: boolean, content: boolean,
): Match {
  let start = at;
  let end = at + len;
  if (spaced) {
    while (start > 0 && !/\s/u.test(prompt[start - 1])) start--;
    while (end < prompt.length && !/\s/u.test(prompt[end])) end++;
  }
  return { start, end, at, len, whole, content };
}

/** Every place `word` (or its longest occurring prefix) turns up in the prompt. */
function matchesFor(prompt: string, hay: string, word: string, spaced: boolean): Match[] {
  const floor = DENSE_SCRIPT.test(word) ? MIN_STEM_DENSE : MIN_STEM;
  for (let n = word.length; n >= 1; n--) {
    // n === word.length is the whole word — exact evidence at any length. Anything shorter is a
    // guess at an inflection or a compound, and below the floor it is not worth making.
    if (n < word.length && n < floor) break;
    const stem = word.slice(0, n).toLowerCase();
    // A stem below the floor only counts where it OPENS a word. It is only here at all because a
    // whole word is exempt from the floor, and that exemption is what lets the one-letter label
    // word of "Serpenti e rettili" match inside every second word of the sentence — each hit then
    // grows out to its whole token and the run creeps outward until it has eaten the prompt.
    // Longer stems stay free to match mid-word, which is how German compounds are found at all:
    // "Raumfahrt" is announced as "Weltraum-Wörter", with the label buried in the middle.
    const mustOpenWord = spaced && n < floor;
    const found: Match[] = [];
    for (let i = hay.indexOf(stem); i !== -1; i = hay.indexOf(stem, i + 1)) {
      if (mustOpenWord && i > 0 && !/\s/u.test(prompt[i - 1])) continue;
      found.push(grow(prompt, i, n, spaced, n === word.length, n >= floor));
    }
    if (found.length > 0) return found;
  }
  return [];
}

function isGlue(gap: string): boolean {
  if (gap.trim().length === 0) return true;
  if (SENTENCE_BREAK.test(gap)) return false;
  const words = gap.split(/[\s,、]+/u).filter(Boolean);
  return words.length <= MAX_GAP_WORDS && words.every((w) => w.length <= MAX_GAP_WORD_LEN);
}

function partsFor(prompt: string, from: number, to: number): PromptParts | null {
  // Punctuation and spacing belong to the sentence, not to the phrase.
  let start = from;
  let end = to;
  let bold = prompt.slice(start, end);
  const tail = bold.replace(TRIM_TAIL, '');
  end -= bold.length - tail.length;
  const head = tail.replace(TRIM_LEAD, '');
  start += tail.length - head.length;
  bold = head;
  if (bold.length === 0) return null;

  const before = prompt.slice(0, start);
  const after = prompt.slice(end);
  // Bolding the entire prompt singles nothing out — it just makes the banner heavier. Happens when
  // a prompt IS its own label, which is what a class synthesized from a bare id looks like.
  if (before.length === 0 && after.length === 0) return null;

  return { before, bold, after };
}

/**
 * Splits `prompt` around the phrase that carries the rule, or returns null when the label leaves no
 * trace in it (in which case the banner should just render the prompt plainly).
 *
 * `emphasis` is the class's optional hand-written override — see the header.
 */
export function emphasise(
  prompt: string,
  label: string | undefined,
  emphasis?: string,
): PromptParts | null {
  if (!prompt) return null;

  // The author's own word on which part carries the rule. A stale one — prompt edited, translation
  // copied from another language — falls through to the matcher rather than blanking the emphasis;
  // promptEmphasis.test.ts fails the build on one that no longer occurs in its prompt.
  if (emphasis) {
    const at = prompt.toLowerCase().indexOf(emphasis.toLowerCase());
    if (at !== -1) return partsFor(prompt, at, at + emphasis.length);
  }

  if (!label) return null;

  const hay = prompt.toLowerCase();
  const spaced = /\s/u.test(prompt);
  // "Wetter & Himmel", "Schlangen & Reptilien" — both halves are searched now. Splitting on the
  // separator and keeping only the head is what lost "und Reptilien" from prompts that spell the
  // pair out; a half that leaves no trace (the bugs class is "Käfer & Spinnen" but its prompt says
  // "Krabbeltiere") simply contributes no match.
  const words = label.split(/[\s&/,]+/u).filter((w) => w.length > 0);
  const groups = words.map((w) => matchesFor(prompt, hay, w, spaced)).filter((g) => g.length > 0);
  if (groups.length === 0) return null;

  // Anchor on the longest literal hit — the match least likely to be a coincidence.
  let anchor = groups[0][0];
  for (const g of groups) for (const m of g) if (m.len > anchor.len) anchor = m;

  let start = anchor.start;
  let end = anchor.end;
  // Extend the run over the label's other words, but only into a match sitting right next to what
  // we already have. "Schlangen und Reptilien" comes along whole; the same word turning up
  // elsewhere in the sentence does not. Repeats until nothing more joins, so a three-word label
  // ("Sonne, Mond und Planeten") assembles one word at a time.
  for (let grew = true; grew;) {
    grew = false;
    for (const g of groups) {
      for (const m of g) {
        if (m.start >= start && m.end <= end) continue;
        if (!m.whole && m.len < MIN_JOIN_STEM) continue;
        const gap = m.start >= end ? prompt.slice(end, m.start)
          : m.end <= start ? prompt.slice(m.end, start)
            : '';
        if (!isGlue(gap)) continue;
        // A glue word of the label ("Salle *de* bain") may only join where it is directly against
        // the run. Letting it reach across other glue picks up the wrong copy of itself: the "de"
        // of "toutes les choses de la salle de bain" is one gap-hop from "salle", and joining that
        // one put "de la" in front of the phrase instead of inside it.
        if (!m.content && gap.trim().length > 0) continue;
        start = Math.min(start, m.start);
        end = Math.max(end, m.end);
        grew = true;
      }
    }
  }

  if (!spaced && end - start > prompt.length * MAX_TOKEN_SHARE) {
    // An unspaced script, where there was no boundary to stop the growth. Fall back to the literal
    // match.
    start = anchor.at;
    end = anchor.at + anchor.len;
  }

  return partsFor(prompt, start, end);
}
